import { useState, useEffect, useCallback } from "react";
import { Trophy, Crown, Medal, Award, Users, TrendingUp } from "lucide-react";
import api from "../../services/api";

function formatNumber(n) {
  if (n == null) return "0";
  return n.toLocaleString("pt-BR");
}

function formatCurrency(v) {
  if (v == null) return "R$ 0,00";
  return "R$ " + v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

var PLACE_ICONS = [Crown, Medal, Award];
var PLACE_COLORS = ["text-amber-500", "text-gray-400", "text-orange-600"];
var PLACE_BG = ["bg-amber-50 border-amber-200", "bg-gray-50 border-gray-200", "bg-orange-50 border-orange-200"];

export default function DoletasRanking() {
  var rankingState = useState([]);
  var ranking = rankingState[0];
  var loadingState = useState(true);
  var roleFilter = useState("");

  var fetchRanking = useCallback(async function () {
    loadingState[1](true);
    try {
      var params = new URLSearchParams();
      params.append("limit", "20");
      if (roleFilter[0]) params.append("role_filter", roleFilter[0]);
      var res = await api.get("/doletas/ranking?" + params.toString());
      rankingState[1](res.data.ranking || []);
    } catch (err) {
      console.error(err);
    } finally {
      loadingState[1](false);
    }
  }, [roleFilter[0]]);

  useEffect(function () { fetchRanking(); }, [fetchRanking]);

  return (
    <div className="space-y-6" data-testid="doletas-ranking-panel">
      <div className="flex items-center gap-3">
        <select value={roleFilter[0]} onChange={function (e) { roleFilter[1](e.target.value); }}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm" data-testid="ranking-role-filter">
          <option value="">Todos</option>
          <option value="client">Clientes</option>
          <option value="broker">Vendedores</option>
        </select>
      </div>

      {/* Top 3 Podium */}
      {ranking.length >= 3 && (
        <div className="grid grid-cols-3 gap-3 lg:gap-4">
          {[1, 0, 2].map(function (idx) {
            var r = ranking[idx];
            if (!r) return null;
            var Icon = PLACE_ICONS[idx] || Trophy;
            var color = PLACE_COLORS[idx] || "text-gray-500";
            var bg = PLACE_BG[idx] || "bg-gray-50 border-gray-200";
            return (
              <div key={r.user_id} className={"border-2 rounded-xl p-4 text-center " + bg + (idx === 0 ? " lg:scale-110 lg:shadow-lg" : "")} data-testid={"podium-" + r.position}>
                <Icon className={"w-8 h-8 mx-auto mb-2 " + color} />
                <p className="text-2xl font-bold text-[#0A2F3D]">{r.position}o</p>
                <p className="font-semibold text-[#0A2F3D] text-sm mt-1 truncate">{r.user_name}</p>
                <p className="text-xs text-gray-400">{r.user_role === "broker" ? "Vendedor" : "Cliente"}</p>
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="text-lg font-bold text-[#F26A4B]">{formatNumber(r.balance)}</p>
                  <p className="text-xs text-gray-500">doletas</p>
                  <p className="text-sm font-medium text-emerald-600 mt-1">{formatCurrency(r.balance_reais)}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Full Ranking Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100">
          <h3 className="font-semibold text-[#0A2F3D] text-sm flex items-center gap-2">
            <Trophy className="w-4 h-4 text-amber-500" />Ranking Completo
          </h3>
        </div>
        {loadingState[0] ? (
          <div className="flex items-center justify-center py-12"><div className="w-6 h-6 border-2 border-[#3B82F6] border-t-transparent rounded-full animate-spin" /></div>
        ) : ranking.length === 0 ? (
          <div className="py-12 text-center text-gray-400 text-sm">Nenhum participante ainda</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="px-4 py-3 text-xs font-medium text-gray-500 w-16">#</th>
                  <th className="px-4 py-3 text-xs font-medium text-gray-500">Nome</th>
                  <th className="px-4 py-3 text-xs font-medium text-gray-500">Tipo</th>
                  <th className="px-4 py-3 text-xs font-medium text-gray-500 text-right">Doletas</th>
                  <th className="px-4 py-3 text-xs font-medium text-gray-500 text-right">R$ Equiv.</th>
                  <th className="px-4 py-3 text-xs font-medium text-gray-500 text-right">Ganhos</th>
                  <th className="px-4 py-3 text-xs font-medium text-gray-500 text-right">Gastos</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {ranking.map(function (r) {
                  return (
                    <tr key={r.user_id} className="hover:bg-gray-50 transition-colors" data-testid={"ranking-row-" + r.position}>
                      <td className="px-4 py-3">
                        <span className={"inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold " +
                          (r.position === 1 ? "bg-amber-100 text-amber-700" :
                           r.position === 2 ? "bg-gray-100 text-gray-700" :
                           r.position === 3 ? "bg-orange-100 text-orange-700" :
                           "bg-gray-50 text-gray-500")}>{r.position}</span>
                      </td>
                      <td className="px-4 py-3 font-medium text-[#0A2F3D]">{r.user_name}</td>
                      <td className="px-4 py-3">
                        <span className={"px-2 py-1 text-xs font-medium rounded-full " + (r.user_role === "broker" ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700")}>
                          {r.user_role === "broker" ? "Vendedor" : "Cliente"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-[#0A2F3D]">{formatNumber(r.balance)}</td>
                      <td className="px-4 py-3 text-right font-medium text-emerald-600">{formatCurrency(r.balance_reais)}</td>
                      <td className="px-4 py-3 text-right text-gray-500">{formatNumber(r.total_earned)}</td>
                      <td className="px-4 py-3 text-right text-gray-500">{formatNumber(r.total_spent)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
